import * as React from 'react';

import { InvoiceStatus } from '@rematter/frontend-lib-api-client';
import { InvoiceRecord } from '@rematter/modules/finance/types';
import {
  cn,
  formatMoney,
  LocalizedDate,
  TinyTableProps,
} from '@rematter/ui-web';

import { useTranslations } from '@/finance/i18n/useTranslations';

type TinyTableColumn<T> = TinyTableProps<T>['columns'][number];

const getStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case 'Pending':
      return 'bg-warning/10 text-warning';
    case 'Paid':
      return 'bg-success/10 text-success';
    case 'Voided':
      return 'bg-neutral/10 text-neutral';
    case 'ProForma':
      return 'bg-primary/10 text-primary';
  }
};

const getStatusLabel = (
  status: InvoiceStatus,
  t: ReturnType<typeof useTranslations>,
): string => {
  switch (status) {
    case 'Pending':
      return t.invoices.list.status.badge.pending;
    case 'Paid':
      return t.invoices.list.status.badge.paid;
    case 'Voided':
      return t.invoices.list.status.badge.voided;
    case 'ProForma':
      return t.invoices.list.status.badge.proForma;
  }
};

export const useRecentInvoicesColumns = () => {
  const t = useTranslations();

  const columns: TinyTableColumn<InvoiceRecord>[] = React.useMemo(
    () => [
      {
        key: 'documentNumber',
        label: t.overview.columns.documentNumber,
        width: '1fr',
        cell: (row) => (
          <span className="font-medium">{row.documentNumber}</span>
        ),
      },
      {
        key: 'accountName',
        label: t.overview.columns.account,
        width: '1fr',
        cell: (row) => row.accountName || '\u2014',
      },
      {
        key: 'dueDate',
        label: t.overview.columns.dueDate,
        width: '1fr',
        cell: (row) =>
          row.dueDate ? (
            <LocalizedDate date={row.dueDate} dateStyle="short" />
          ) : (
            '\u2014'
          ),
      },
      {
        key: 'amount',
        label: t.overview.columns.amount,
        width: 'auto',
        align: 'right' as const,
        cell: (row) => formatMoney(row.amount.amount, row.amount.currencyCode),
      },
      {
        key: 'status',
        label: t.overview.columns.status,
        width: 'auto',
        cell: (row) => (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              getStatusColor(row.status),
            )}
          >
            {getStatusLabel(row.status, t)}
          </span>
        ),
      },
    ],
    [t],
  );

  return { columns };
};
